import type { ChartActionConfig, ChartActionId, ChartData, ChartTemplateConfig } from '../../types/dashboard';

const DEFAULT_ACTIONS: ChartActionConfig[] = [
  { id: 'view_chart', label: 'Chart', enabled: true, placement: 'primary' },
  { id: 'view_table', label: 'Table', enabled: true, placement: 'primary' },
  { id: 'sort', label: 'Sort', enabled: true, placement: 'primary' },
  { id: 'top_n', label: 'Top N', enabled: true, placement: 'primary', options: { values: [3, 5, 10, 20, 'all'] } },
  { id: 'filter', label: 'Filter', enabled: false, placement: 'primary' },
  { id: 'reset', label: 'Reset', enabled: true, placement: 'primary' },
  { id: 'download_csv', label: 'CSV', enabled: true, placement: 'menu' },
  { id: 'copy_chart', label: 'Copy chart', enabled: true, placement: 'menu' },
  { id: 'copy_table', label: 'Copy table', enabled: true, placement: 'menu' },
  { id: 'copy_table_image', label: 'Copy table image', enabled: true, placement: 'menu' },
  { id: 'expand', label: 'Expand', enabled: true, placement: 'primary' },
  { id: 'pop_out', label: 'Pop out', enabled: true, placement: 'primary' },
  { id: 'drill_down', label: 'Details', enabled: true, placement: 'menu' },
  { id: 'feedback', label: 'Feedback', enabled: true, placement: 'menu' },
  { id: 'hide', label: 'Hide', enabled: true, placement: 'menu' },
  { id: 'restore', label: 'Restore', enabled: true, placement: 'menu' },
];

function normalizeAction(action: ChartActionConfig): ChartActionConfig {
  return {
    ...action,
    enabled: action.enabled !== false,
  };
}

export function getDefaultChartActions(): ChartActionConfig[] {
  return DEFAULT_ACTIONS.map(normalizeAction);
}

export function getChartActions(chart: ChartData): ChartActionConfig[] {
  const templateActions = chart.template?.actions;
  if (!Array.isArray(templateActions) || templateActions.length === 0) {
    return getDefaultChartActions();
  }

  const defaultsById = new Map(DEFAULT_ACTIONS.map((action) => [action.id, action]));
  return templateActions
    .map((action) => normalizeAction({ ...defaultsById.get(action.id), ...action }))
    .filter((action) => action.enabled !== false);
}

export function isChartActionEnabled(chart: ChartData, actionId: ChartActionId): boolean {
  return getChartActions(chart).some((action) => action.id === actionId && action.enabled !== false);
}

export function resolveChartTemplate(chart: ChartData): Required<Pick<ChartTemplateConfig, 'chartType' | 'title' | 'data'>> & ChartTemplateConfig {
  const template = chart.template || {};
  const axes = template.axes || {};

  return {
    ...template,
    chartType: template.chartType || chart.type,
    title: template.title || chart.title,
    data: Array.isArray(template.data) ? template.data : chart.data,
    axes: {
      ...axes,
      x: {
        ...axes.x,
        label: axes.x?.label || chart.x_axis_label,
      },
      y: {
        ...axes.y,
        label: axes.y?.label || chart.y_axis_label,
      },
    },
    actions: getChartActions(chart),
  };
}

export function chartFromTemplate(chart: ChartData): ChartData {
  const resolved = resolveChartTemplate(chart);
  const metricDisplayNames = { ...(chart.metric_display_names || {}) };

  resolved.series?.forEach((series) => {
    if (series.label) metricDisplayNames[series.field] = series.label;
  });

  return {
    ...chart,
    type: resolved.chartType as ChartData['type'],
    title: resolved.title,
    data: resolved.data,
    x_axis_label: resolved.axes?.x?.label || chart.x_axis_label,
    y_axis_label: resolved.axes?.y?.label || chart.y_axis_label,
    metric_display_names: Object.keys(metricDisplayNames).length > 0 ? metricDisplayNames : chart.metric_display_names,
  };
}
